/**
 * AtlasCityPage — Sprint 4B · Page 3 (city)
 * Reuses the itinerary's Leaflet map (lazy import, OSM tiles) to show, read-only:
 * visited sites, the gems the traveller FOUND (never the unfound — no spoiler),
 * and the city postcard if it's unlocked. Online tiles are acceptable here.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildCityView } from '../../utils/atlasMap';
import PostcardCard from '../PostcardCard.jsx';
import theme from '../../styles/theme';

export default function AtlasCityPage({ country, city }) {
  const { t } = useTranslation('common');
  const [data, setData] = useState(null);
  const [leaflet, setLeaflet] = useState(null);
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    buildCityView(country, city).then(d => { if (!cancelled) setData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [country, city]);

  // Lazy-load Leaflet (vanilla) + its CSS, same as the itinerary planner.
  useEffect(() => {
    if (leaflet || typeof navigator === 'undefined' || !navigator.onLine) return;
    import('leaflet').then(mod => {
      const L = mod.default || mod;
      if (!document.querySelector('link[data-leaflet-css]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.setAttribute('data-leaflet-css', '');
        document.head.appendChild(link);
      }
      setLeaflet(L);
    }).catch(() => {});
  }, [leaflet]);

  // Mount the map once Leaflet + data ready.
  useEffect(() => {
    const L = leaflet;
    if (!L || !containerRef.current || !data || !data.center || mapRef.current) return;

    const map = L.map(containerRef.current, { zoomControl: true });
    map.setView([data.center.lat, data.center.lng], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap', maxZoom: 19,
    }).addTo(map);

    const pts = [];
    for (const s of data.sites) {
      L.circleMarker([s.lat, s.lng], {
        radius: s.visited ? 8 : 5,
        fillColor: s.visited ? theme.blue : '#cdbfa8',
        color: 'white', weight: s.visited ? 2 : 1,
        fillOpacity: s.visited ? 0.95 : 0.5,
      }).addTo(map).bindTooltip(s.name, { direction: 'top' });
      pts.push([s.lat, s.lng]);
    }
    for (const g of data.gems) {
      L.circleMarker([g.lat, g.lng], {
        radius: 9, fillColor: theme.terracotta, color: '#fff', weight: 2, fillOpacity: 1,
      }).addTo(map).bindTooltip(`✦ ${g.name}`, { direction: 'top' });
      pts.push([g.lat, g.lng]);
    }

    mapRef.current = map;
    setTimeout(() => {
      map.invalidateSize();
      if (pts.length) map.fitBounds(L.latLngBounds(pts), { padding: [30, 30], maxZoom: 15 });
    }, 200);

    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [leaflet, data]);

  const offline = typeof navigator !== 'undefined' && !navigator.onLine;

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <h2 style={S.title}>{city}</h2>
        {data?.postcardUnlocked && (
          <div style={S.postcard}><PostcardCard city={city} country={country} size="compact" /></div>
        )}
      </div>

      {data && !data.center ? (
        <div style={S.empty}>{t('atlasBook.cityEmpty')}</div>
      ) : offline ? (
        <div style={S.empty}>{t('atlasBook.cityOffline')}</div>
      ) : (
        <div ref={containerRef} style={S.map} />
      )}

      <div style={S.legend}>
        <span style={S.legendItem}><span style={{ ...S.dot, background: theme.blue }} /> {t('atlasBook.sitesVisited')}</span>
        <span style={S.legendItem}><span style={{ ...S.dot, background: theme.terracotta }} /> {t('atlasBook.gemsFound')}</span>
      </div>
    </div>
  );
}

const S = {
  wrap: { width: '100%' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 },
  title: { margin: 0, fontSize: 22, fontWeight: 800, color: theme.brownDark, fontFamily: "'Noto Serif', serif" },
  postcard: { width: 96, flex: '0 0 auto' },
  map: { width: '100%', height: '48vh', borderRadius: 14, overflow: 'hidden', border: `1px solid ${theme.creamDeep}` },
  empty: { textAlign: 'center', color: theme.brown, fontSize: 14, fontStyle: 'italic', padding: '40px 20px', background: theme.cream, borderRadius: 14 },
  legend: { display: 'flex', justifyContent: 'center', gap: 18, marginTop: 12 },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.brown },
  dot: { width: 12, height: 12, borderRadius: '50%', display: 'inline-block' },
};
